
import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Link } from "react-router-dom";

interface CalculatorProps {
  stockData: any[];
  symbol: string;
}

export const InvestmentCalculator = ({ stockData, symbol }: CalculatorProps) => {
  const [investment, setInvestment] = useState("1000");
  const [years, setYears] = useState("5");

  // Calculate maximum available years from stock data
  const maxYears = Math.floor(stockData.length / 12);
  
  // Common year periods for toggle
  const yearOptions = [1, 5, 10, 20].filter(year => year <= maxYears);

  // Preset investment amounts
  const presetAmounts = [1000, 5000, 10000, 50000, 100000];

  // Ensure selected years doesn't exceed maximum
  useEffect(() => {
    if (parseInt(years) > maxYears) {
      setYears(maxYears.toString());
    }
  }, [maxYears, years]);

  const calculateReturns = () => {
    if (!stockData || stockData.length < 2) return { totalReturn: 0, annualReturn: 0 };

    // Calculate the index for the historical price based on years
    const yearsInMonths = Math.min(parseInt(years) * 12, stockData.length - 1);
    const historicalIndex = Math.min(yearsInMonths, stockData.length - 1);

    // Get prices
    const historicalPrice = parseFloat(stockData[historicalIndex].close);
    const currentPrice = parseFloat(stockData[0].close);
    const initialInvestment = parseFloat(investment);

    // Calculate number of shares that could have been bought
    const shares = initialInvestment / historicalPrice;
    
    // Calculate current value of investment
    const currentValue = shares * currentPrice;
    
    // Calculate returns
    const totalReturn = ((currentValue - initialInvestment) / initialInvestment) * 100;
    const yearsHeld = parseFloat(years);
    const annualReturn = ((Math.pow((currentValue / initialInvestment), 1/yearsHeld)) - 1) * 100;

    return {
      totalReturn: totalReturn.toFixed(2),
      annualReturn: annualReturn.toFixed(2),
      finalValue: currentValue.toFixed(2)
    };
  };

  const returns = calculateReturns();

  // Related stocks based on sector
  const getRelatedStocks = () => {
    const techStocks = ["AAPL", "GOOGL", "META", "NVDA"];
    const financeStocks = ["JPM", "V", "BAC", "MA"];
    const retailStocks = ["AMZN", "WMT", "TGT", "COST"];
    
    let relatedStocks = techStocks;
    if (financeStocks.includes(symbol?.toUpperCase())) {
      relatedStocks = financeStocks;
    } else if (retailStocks.includes(symbol?.toUpperCase())) {
      relatedStocks = retailStocks;
    }
    
    return relatedStocks.filter(stock => stock !== symbol?.toUpperCase()).slice(0, 3);
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <Card className="p-8 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900">
        <h2 className="text-3xl font-bold text-center space-y-4">
          <div className="text-muted-foreground text-xl mb-4">If you had invested</div>
          <div className="text-4xl text-blue-600 dark:text-blue-400 font-bold mb-2">
            ${Number(investment).toLocaleString()}
          </div>
          <div className="text-muted-foreground text-xl mb-4">
            in {symbol.toUpperCase()} {years} years ago, you would have
          </div>
          <div className="text-5xl text-green-600 dark:text-green-400 font-bold">
            ${Number(returns.finalValue).toLocaleString()}
          </div>
          <div className="text-muted-foreground text-xl mt-2">today</div>
        </h2>
      </Card>

      <Card className="p-6 space-y-6 shadow-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Label htmlFor="investment" className="text-lg">Initial Investment ($)</Label>
            <Input
              id="investment"
              type="number"
              value={investment}
              onChange={(e) => setInvestment(e.target.value)}
              className="text-lg h-12"
            />
            <div className="flex flex-wrap gap-2">
              {presetAmounts.map((amount) => (
                <button
                  key={amount}
                  onClick={() => setInvestment(amount.toString())}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors
                    ${investment === amount.toString()
                      ? 'bg-blue-600 text-white dark:bg-blue-500'
                      : 'bg-secondary hover:bg-blue-100 dark:hover:bg-blue-900'
                    }`}
                >
                  ${amount.toLocaleString()}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            <Label className="text-lg">Investment Period (Years)</Label>
            <ToggleGroup 
              type="single" 
              value={years}
              onValueChange={(value) => value && setYears(value)}
              className="justify-start"
            >
              {yearOptions.map((year) => (
                <ToggleGroupItem 
                  key={year} 
                  value={year.toString()}
                  className={`text-lg px-6 ${
                    years === year.toString() ? 'bg-blue-600 text-white dark:bg-blue-500' : ''
                  }`}
                >
                  {year}Y
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            <Input
              id="years"
              type="number"
              value={years}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (value <= maxYears) {
                  setYears(e.target.value);
                }
              }}
              max={maxYears}
              className="text-lg h-12"
            />
            <p className="text-sm text-muted-foreground">
              Maximum available period: {maxYears} years
            </p>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-8 hover:shadow-lg transition-shadow">
          <h2 className="text-xl font-semibold text-muted-foreground mb-4">Total Return of the Investment</h2>
          <div className="flex items-baseline gap-1">
            <p className="text-4xl font-bold text-blue-600 dark:text-blue-400">
              {returns.totalReturn}
            </p>
            <span className="text-2xl font-semibold text-blue-600 dark:text-blue-400">%</span>
          </div>
        </Card>
        <Card className="p-8 hover:shadow-lg transition-shadow">
          <h2 className="text-xl font-semibold text-muted-foreground mb-4">Annual Return of the Investment</h2>
          <div className="flex items-baseline gap-1">
            <p className="text-4xl font-bold text-blue-600 dark:text-blue-400">
              {returns.annualReturn}
            </p>
            <span className="text-2xl font-semibold text-blue-600 dark:text-blue-400">%</span>
          </div>
        </Card>
      </div>

      {/* Related Calculators Section */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Compare with Other Stock Calculators</h3>
        <div className="flex gap-4 flex-wrap">
          {getRelatedStocks().map((relatedSymbol) => (
            <Link
              key={relatedSymbol}
              to={`/stock/${relatedSymbol}`}
              className="inline-flex items-center px-4 py-2 bg-blue-50 dark:bg-blue-900 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800 transition-colors"
            >
              {relatedSymbol} Calculator
            </Link>
          ))}
        </div>
      </Card>
    </div>
  );
};
